/**
 * DI-3-F — ONE upload pass of ONE set (story §2.1 `uploader.ts`): the blocks,
 * the manifest, the poll. The runner decides WHEN a pass runs (N2) and what
 * follows it (the retry pass, "failed"); this module does the pass.
 *
 *   1. `uploading`   every file of the pass the ledger does not hold goes up
 *                    (`blockUpload.ts`, through the tab-wide queue); each
 *                    201 commit enters the ledger at once (persisted — a tab
 *                    that dies now resumes after it). A file whose PUTs fail
 *                    after the retries is not committed, and the pass ends
 *                    there (`put_failed`) WITHOUT a manifest: completing one
 *                    would only have the ingest report the file absent, a
 *                    failed run for nothing;
 *   2. `committing`  `completeUploadSession` with the manifest of the pass
 *                    (`manifest.ts:passManifest` — every file of the set the
 *                    server has not verified yet) → `jobId`, persisted;
 *   3. `ingesting`   `getUploadSession` every 5 s until COMPLETED / FAILED.
 *
 * A pass that died after its complete (a reload while `ingesting`) resumes at
 * 3 with the persisted session; a session the gateway no longer knows (404 —
 * the session record is in-memory there until the persistent table exists)
 * is completed again on a new session: the same manifest is the same job, so
 * the poll finds the run that already verified the files (DI-1-6 dedup).
 *
 * Anything but a PUT failing (the session open, the complete, the poll) is a
 * TRANSPORT stop: it throws `UploadTransportError` and the runner tries the
 * pass again later from where the ledger stands (edge ak).
 */
import type { UploadApi } from "./api.js";
import { FileUploadFailed, uploadFile, type FetchLike } from "./blockUpload.js";
import {
  commit,
  committedBytes,
  isCommitted,
  pendingFiles,
  type Ledger,
} from "./ledger.js";
import {
  manifestOf,
  pollUntilDone,
  POLL_MS,
  type PollOutcome,
  type UploadFile,
} from "./manifest.js";
import type { UploadQueue } from "./queue.js";
import { SasSource, type OpenedSession } from "./sas.js";
import type { SetUpload } from "./state.js";
import { isInspection3FetchError } from "../api/inspection3Fetch.js";

/** Files of one set in flight at once (the queue still caps the PUTs). */
export const FILE_WINDOW = 4;

/**
 * M5-NODE-01 — the server REFUSED the upload session (409 `upload_blocked`,
 * 400 `reason_required`): no retry fixes it — the driver stops the upload
 * with these words (`refusalWords`).
 */
export class UploadRefusedError extends Error {
  constructor(
    message: string,
    readonly reason: UploadRefusal,
  ) {
    super(message);
  }
}

/**
 * The server's refusals no retry fixes. DI-3-S68 T1 — besides M5-NODE-01's
 * two: `da_state_not_uploadable` (409 — the set no longer accepts images:
 * approved and finalized, rejected, withdrawn) and `set_gone` (a 404 on the
 * session open — the set was withdrawn / is not this tenant's).
 */
export type UploadRefusal =
  | "upload_blocked"
  | "reason_required"
  | "da_state_not_uploadable"
  | "set_gone";

/** DI-3-S68 T1 — the refusals that end the set's upload for good (the lane drops the set). */
export const FINAL_REFUSALS: ReadonlySet<UploadRefusal> = new Set([
  "da_state_not_uploadable",
  "set_gone",
]);

/** The pilot's words for the refusals. */
export function refusalWords(reason: UploadRefusal): string {
  switch (reason) {
    case "upload_blocked":
      return "Image upload is switched off for this workflow.";
    case "reason_required":
      return "The server asks for a reason for this upload — start it again from Upload images….";
    case "da_state_not_uploadable":
      return "This data set no longer accepts images — the upload ended.";
    case "set_gone":
      return "This data set is no longer on the platform (withdrawn) — the upload ended.";
  }
}

/**
 * A refusal the server ANSWERED for good — never retried. `step` "open"
 * (the upload session's open): a 404 means the set is gone; on the
 * complete a 404 is the gateway's forgotten session (a transport stop,
 * handled by a new session).
 */
export function refusalOf(
  e: unknown,
  step: "open" | "complete" = "open",
): UploadRefusedError | null {
  if (!isInspection3FetchError(e)) return null;
  const reason = (e.body as { reason?: unknown } | null)?.reason;
  if (
    (e.status === 409 && reason === "upload_blocked") ||
    (e.status === 400 && reason === "reason_required") ||
    (e.status === 409 && reason === "da_state_not_uploadable")
  )
    return new UploadRefusedError(refusalWords(reason), reason);
  if (e.status === 404 && step === "open")
    return new UploadRefusedError(refusalWords("set_gone"), "set_gone");
  return null;
}

export class UploadTransportError extends Error {
  constructor(
    message: string,
    readonly status: number | null,
  ) {
    super(message);
  }
}

export interface PassContext {
  daId: string;
  /** The files of THIS pass (the set's, or the retry's subset). */
  files: readonly UploadFile[];
  /** The upload as persisted (ledger, session, job). */
  upload: SetUpload;
  /** The set's place in the Start order (the queue key's head). */
  setOrder: number;
  /** The bytes of a row of this tab's index; null when the tab has no file for it. */
  fileOf: (index: number) => Blob | null;
  api: UploadApi;
  queue: UploadQueue;
  now: () => Date;
  sleep: (ms: number) => Promise<void>;
  fetch?: FetchLike | undefined;
  pollMs?: number | undefined;
  /** Apply a change to the set's upload; `persist` = write it to the record. */
  update: (patch: Partial<SetUpload>, persist: boolean) => Promise<void>;
  /** M5-NODE-01 — the escape hatch's reason, sent on every session open. */
  reason?: string | null | undefined;
}

export type PassResult =
  | { kind: "completed"; outcome: PollOutcome }
  | { kind: "failed"; outcome: PollOutcome }
  /** Files whose PUTs failed after the retries (no manifest was completed). */
  | { kind: "put_failed"; files: string[] };

function transport(e: unknown, what: string): UploadTransportError {
  if (e instanceof UploadTransportError) return e;
  const status = isInspection3FetchError(e) ? e.status : null;
  return new UploadTransportError(
    `The upload stopped — ${what}${
      status !== null ? ` (HTTP ${String(status)})` : " (no connection)"
    }; retrying.`,
    status,
  );
}

export async function runUploadPass(ctx: PassContext): Promise<PassResult> {
  const hint = {
    expectedFileCount: ctx.files.length,
    expectedTotalBytes: ctx.files.reduce((n, f) => n + f.sizeBytes, 0),
    ...(ctx.reason ? { reason: ctx.reason } : {}),
  };
  let u = ctx.upload;
  const set = async (patch: Partial<SetUpload>, persist: boolean) => {
    u = { ...u, ...patch };
    await ctx.update(patch, persist);
  };

  // 3 — a pass that already completed its manifest: poll it
  if (u.jobId && u.sessionId && u.phase === "ingesting") {
    let polled: PassResult | null;
    try {
      polled = await pollSession(ctx, u.sessionId, set);
    } catch (e) {
      if (isInspection3FetchError(e) && e.status === 404) polled = null;
      else throw transport(e, "the verification could not be read");
    }
    if (polled) return polled;
    // the gateway forgot the session: complete the same manifest again
    await set({ phase: "committing", jobId: null, sessionId: null }, true);
  }

  const sas = new SasSource(
    () =>
      ctx.api.open(ctx.daId, hint).catch((e: unknown) => {
        throw (
          refusalOf(e) ?? transport(e, "the upload session could not be opened")
        );
      }),
    () => ctx.now().getTime(),
    (s: OpenedSession) => {
      void set({ sessionId: s.sessionId }, true);
    },
  );

  // 1 — the bytes
  if (u.phase !== "committing") {
    let ledger: Ledger = u.ledger;
    const todo = pendingFiles(ledger, ctx.files);
    await set(
      {
        phase: "uploading",
        bytesTotal: hint.expectedTotalBytes,
        bytesSent: committedBytes(ledger, ctx.files),
        filesTotal: ctx.files.length,
        filesDone: ctx.files.filter((f) => isCommitted(ledger, f)).length,
        error: null,
        retryAt: null,
      },
      true,
    );
    if (todo.length > 0) {
      const opened = await sas.get();
      let blockSize = opened.blockSize;
      let sent = u.bytesSent;
      let sinceStart = u.startedAt ? (u.sentSinceStart ?? 0) : 0;
      let k = 0;
      let stop: unknown = null;
      const putFailed: string[] = [];
      const next = async (): Promise<void> => {
        while (stop === null) {
          const i = k++;
          const f = todo[i];
          if (!f) return;
          const file = ctx.fileOf(f.index);
          if (!file) {
            putFailed.push(f.filename); // not in this tab's folder
            continue;
          }
          try {
            await uploadFile({
              file,
              filename: f.filename,
              md5Hex: f.md5Hex,
              blockSize,
              sasUrl: async () => {
                const s = await sas.get();
                blockSize = s.blockSize;
                return s.url;
              },
              onForbidden: (url) => {
                sas.invalidate(url);
              },
              queue: ctx.queue,
              key: [ctx.setOrder, f.index],
              ...(ctx.fetch ? { fetch: ctx.fetch } : {}),
              sleep: ctx.sleep,
              onBytes: (n) => {
                sent += n;
                sinceStart += n;
                void set(
                  {
                    bytesSent: Math.min(sent, hint.expectedTotalBytes),
                    sentSinceStart: sinceStart,
                    ...(u.startedAt
                      ? {}
                      : { startedAt: ctx.now().toISOString() }),
                  },
                  false,
                );
              },
            });
            ledger = commit(ledger, f, ctx.now().toISOString());
            await set(
              {
                ledger,
                filesDone: ctx.files.filter((x) => isCommitted(ledger, x))
                  .length,
                bytesSent: Math.max(
                  u.bytesSent,
                  committedBytes(ledger, ctx.files),
                ),
              },
              true,
            );
          } catch (e) {
            if (e instanceof FileUploadFailed) {
              putFailed.push(f.filename);
              continue;
            }
            stop = e;
          }
        }
      };
      await Promise.all(
        Array.from({ length: Math.min(FILE_WINDOW, todo.length) }, next),
      );
      if (stop !== null) throw transport(stop, "a block could not be sent");
      if (putFailed.length > 0) return { kind: "put_failed", files: putFailed };
    }
  }

  // 2 — the manifest
  if (ctx.files.length === 0)
    throw new UploadTransportError(
      "The upload waits for the card folder to be read again.",
      null,
    );
  await set({ phase: "committing" }, true);
  const session = await sas.get();
  let accepted: { jobId: string };
  try {
    accepted = await ctx.api.complete(
      ctx.daId,
      session.sessionId,
      manifestOf(ctx.files),
    );
  } catch (e) {
    throw (
      refusalOf(e, "complete") ?? transport(e, "the manifest was not accepted")
    );
  }
  await set(
    {
      phase: "ingesting",
      sessionId: session.sessionId,
      jobId: accepted.jobId,
      processed: 0,
      total: ctx.files.length,
    },
    true,
  );

  // 3 — the verification
  try {
    return await pollSession(ctx, session.sessionId, set);
  } catch (e) {
    throw transport(e, "the verification could not be read");
  }
}

async function pollSession(
  ctx: PassContext,
  sessionId: string,
  set: (patch: Partial<SetUpload>, persist: boolean) => Promise<void>,
): Promise<PassResult> {
  const outcome = await pollUntilDone({
    poll: () => ctx.api.poll(ctx.daId, sessionId),
    onProgress: (s) => {
      void set(
        {
          processed: s.processed,
          total: s.total,
          dataAcquisitionState: s.dataAcquisitionState ?? null,
        },
        false,
      );
    },
    sleep: ctx.sleep,
    intervalMs: ctx.pollMs ?? POLL_MS,
  });
  return outcome.state === "COMPLETED"
    ? { kind: "completed", outcome }
    : { kind: "failed", outcome };
}
